// btw-front/src/pages/PartnerNotices.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import Container from "../components/ui/Container";
import { Badge } from "../components/ui/Badge";
import { Input } from "../components/ui/Input";
import { EmptyState } from "../components/ui/EmptyState";
import { list as listNotices } from "../api/notices";

export default function PartnerNotices() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(
      () => {
        listNotices({ q: q.trim() || undefined })
          .then((rows) => {
            if (!cancelled) setItems(rows || []);
          })
          .catch(() => {
            if (!cancelled) setItems([]);
          })
          .finally(() => {
            if (!cancelled) setLoading(false);
          });
      },
      q ? 300 : 0,
    );
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q]);

  function open(id) {
    navigate(`/partners/notices/${id}`);
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 py-10 md:py-14">
        <Container>
          <header className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 md:text-3xl">
                {t("partners.notices.title")}
              </h1>
              <p className="mt-1 text-sm text-slate-600">{t("partners.notices.subtitle")}</p>
            </div>
            <Link to="/partners/dashboard" className="btw-btn-ghost">
              {t("partners.notices.backToDashboard")}
            </Link>
          </header>

          <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-3">
            <Input
              name="noticeSearch"
              placeholder={t("partners.notices.searchPlaceholder")}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </section>

          <section className="mt-4">
            {loading ? (
              <p className="py-10 text-center text-sm text-slate-400">{t("common.loading")}</p>
            ) : items.length === 0 ? (
              <EmptyState
                title={t("partners.notices.emptyTitle")}
                desc={t("partners.notices.emptyDesc")}
              />
            ) : (
              <>
                {/* 데스크탑 테이블 */}
                <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white md:block">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.notices.table.title")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.notices.table.author")}
                        </th>
                        <th className="px-4 py-3 text-left font-medium">
                          {t("partners.notices.table.createdAt")}
                        </th>
                        <th className="px-4 py-3 text-right font-medium">
                          {t("partners.notices.table.attachments")}
                        </th>
                        <th className="px-4 py-3 text-right font-medium">
                          {t("partners.notices.table.views")}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((n) => (
                        <tr
                          key={n.id}
                          onClick={() => open(n.id)}
                          className="cursor-pointer transition hover:bg-slate-50"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {n.isPinned && (
                                <Badge tone="neutral">{t("partners.notices.pinnedTag")}</Badge>
                              )}
                              <span className="font-medium text-slate-900">{n.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{n.authorName || "-"}</td>
                          <td className="px-4 py-3 text-slate-500">
                            {n.createdAt && String(n.createdAt).slice(0, 10)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {n.attachmentCount || 0}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                            {n.viewCount || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 모바일 카드 */}
                <div className="grid gap-2 md:hidden">
                  {items.map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => open(n.id)}
                      className="flex flex-col gap-1.5 rounded-xl border border-slate-200 bg-white p-3 text-left"
                    >
                      <div className="flex items-center gap-2">
                        {n.isPinned && (
                          <Badge tone="neutral">{t("partners.notices.pinnedTag")}</Badge>
                        )}
                        <span className="text-sm font-medium text-slate-900">{n.title}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>
                          {n.authorName || "-"} · {n.createdAt && String(n.createdAt).slice(0, 10)}
                        </span>
                        <span>
                          {t("partners.notices.table.attachments")} {n.attachmentCount || 0}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            )}
          </section>
        </Container>
      </main>
      <Footer />
    </div>
  );
}
